import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { format } from 'date-fns';

export function BookingWidget({ variant = 'default' }) {
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState('2');

  const handleSearch = () => {
    // Navigate to internal booking page with parameters
    const params = new URLSearchParams();
    if (checkIn) params.append('checkin', format(checkIn, 'yyyy-MM-dd'));
    if (checkOut) params.append('checkout', format(checkOut, 'yyyy-MM-dd'));
    params.append('guests', guests);
    window.location.href = `/book?${params.toString()}`;
  };

  const isCompact = variant === 'compact';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`
        ${isCompact 
          ? 'bg-card/95 backdrop-blur-md rounded-sm p-3 sm:p-4' 
          : 'bg-card/95 backdrop-blur-md rounded-sm p-4 sm:p-6 shadow-2xl shadow-emerald-900/20'
        }
      `}
      data-testid="booking-widget"
    >
      <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {/* Check-in Date */}
        <div className="space-y-1.5 sm:space-y-2">
          <label className="overline block text-[10px] sm:text-xs">Check-in</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal h-10 sm:h-12 bg-background border-border hover:bg-muted text-sm"
                data-testid="checkin-date-btn"
              >
                <Calendar className="mr-2 h-4 w-4 text-emerald-700 dark:text-emerald-400 flex-shrink-0" />
                <span className="truncate">
                  {checkIn ? format(checkIn, 'MMM dd, yyyy') : <span className="text-muted-foreground">Select date</span>}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="single"
                selected={checkIn}
                onSelect={setCheckIn}
                disabled={(date) => date < new Date()}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Check-out Date */}
        <div className="space-y-1.5 sm:space-y-2">
          <label className="overline block text-[10px] sm:text-xs">Check-out</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal h-10 sm:h-12 bg-background border-border hover:bg-muted text-sm"
                data-testid="checkout-date-btn"
              >
                <Calendar className="mr-2 h-4 w-4 text-emerald-700 dark:text-emerald-400 flex-shrink-0" />
                <span className="truncate">
                  {checkOut ? format(checkOut, 'MMM dd, yyyy') : <span className="text-muted-foreground">Select date</span>}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="single"
                selected={checkOut}
                onSelect={setCheckOut}
                disabled={(date) => date < (checkIn || new Date())}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Guests */}
        <div className="space-y-1.5 sm:space-y-2">
          <label className="overline block text-[10px] sm:text-xs">Guests</label>
          <Select value={guests} onValueChange={setGuests}>
            <SelectTrigger 
              className="h-10 sm:h-12 bg-background border-border text-sm"
              data-testid="guests-select"
            >
              <div className="flex items-center">
                <Users className="mr-2 h-4 w-4 text-emerald-700 dark:text-emerald-400 flex-shrink-0" />
                <SelectValue placeholder="Guests" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6].map((num) => (
                <SelectItem key={num} value={num.toString()}>
                  {num} {num === 1 ? 'Guest' : 'Guests'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Search Button */}
        <div className="space-y-1.5 sm:space-y-2">
          <label className="overline block invisible text-[10px] sm:text-xs hidden sm:block">Action</label>
          <Button
            onClick={handleSearch}
            className="w-full h-10 sm:h-12 btn-emerald text-xs sm:text-sm"
            data-testid="check-availability-btn"
          >
            <Search className="mr-2 h-4 w-4" />
            Check Availability
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
